import createDOMPurify from "dompurify"
import { bbcodeToHtml, looksLikeBbcode } from "./bbcode-to-html"
import { normalizeExternalHref } from "./external-href"

// DOMPurify needs a DOM `window`. In the browser that's implicit; in SSR
// (Node) we back it with jsdom. Vite tree-shakes the SSR branch out of the
// client bundle via `import.meta.env.SSR`.
let DOMPurify
if (import.meta.env.SSR) {
  const { JSDOM } = await import("jsdom")
  DOMPurify = createDOMPurify(new JSDOM("").window)
} else {
  DOMPurify = createDOMPurify(window)
}

// Bug #539, second surface: article bodies migrated from Bubble carry anchors
// with a scheme-less href (`<a href="www.wecolabor.com">`), which the browser
// resolves against the article path and lands the reader on the 404 page. Every
// body already passes through DOMPurify on render, so repairing the href here
// fixes them all without touching the stored HTML.
//
// Registered once at module scope, not inside sanitizeContentHtml: addHook
// appends, so a per-call registration would leak a growing hook chain.
// normalizeExternalHref returns a mailto: or any non-http href unchanged, so
// the mail links and DOMPurify's own scheme filtering are untouched.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName !== "A" || !node.hasAttribute("href")) return

  const href = node.getAttribute("href")
  const normalized = normalizeExternalHref(href)
  if (normalized !== href) node.setAttribute("href", normalized)
})

const ALLOWED_TAGS = [
  "p",
  "div",
  "span",
  "h1",
  "h2",
  "h3",
  "strong",
  "em",
  "u",
  "code",
  "sup",
  "sub",
  "a",
  "ul",
  "ol",
  "li",
  "blockquote",
  "hr",
  "br",
  "table",
  "thead",
  "tbody",
  "tr",
  "th",
  "td",
  "img",
]

const ALLOWED_ATTR = [
  "href",
  "target",
  "rel",
  "src",
  "alt",
  "width",
  "height",
  "class",
  "colspan",
  "rowspan",
]

export function sanitizeAiHtml(html) {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
  })
}

export function sanitizeContentHtml(html) {
  const preprocessed = looksLikeBbcode(html) ? bbcodeToHtml(html) : html
  return DOMPurify.sanitize(preprocessed, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
  })
}
